import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import {
  Briefcase, CurrencyDollar, Wrench, Warning, ArrowLeft, CircleNotch,
} from "@phosphor-icons/react";

const fmtMoney = (v) => "$" + Math.round(v || 0).toLocaleString();

function NameCell({ name, to, navigate }) {
  if (to)
    return <button onClick={(e) => { e.stopPropagation(); navigate(to); }}
      className="text-left font-semibold text-[#0055FF] hover:underline" data-testid="leaderboard-name-link">{name}</button>;
  return <span className="font-semibold text-[#0A0A0A]">{name}</span>;
}

const META = {
  "clients-jobs": { icon: Briefcase, accent: "text-[#0055FF]" },
  "clients-revenue": { icon: CurrencyDollar, accent: "text-[#059669]" },
  "technicians-jobs": { icon: Wrench, accent: "text-[#7C3AED]" },
  warnings: { icon: Warning, accent: "text-[#D97706]" },
};

const BAND = {
  Critical: "bg-[#DC2626]/10 text-[#DC2626]",
  High: "bg-[#D97706]/10 text-[#D97706]",
  Monitor: "bg-[#0055FF]/10 text-[#0055FF]",
  Healthy: "bg-[#059669]/10 text-[#059669]",
};

function Header({ rank, children, right }) {
  return (
    <tr className="border-b border-neutral-200 bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
      <th className="px-4 py-3 text-left w-12">#</th>
      {children}
    </tr>
  );
}

export default function Leaderboards() {
  const { metric } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const meta = META[metric] || META["clients-jobs"];
  const Icon = meta.icon;

  useEffect(() => {
    setData(null);
    api.get("/dashboard/leaderboard", { params: { metric } })
      .then((r) => setData(r.data))
      .catch(() => setData(false));
  }, [metric]);

  const rows = data?.rows || [];

  const renderHead = () => {
    if (metric === "clients-jobs")
      return <><th className="px-4 py-3 text-left">Client</th><th className="px-4 py-3 text-right">Jobs</th></>;
    if (metric === "clients-revenue")
      return <><th className="px-4 py-3 text-left">Client</th><th className="px-4 py-3 text-right">Hours</th><th className="px-4 py-3 text-right">Revenue</th></>;
    if (metric === "technicians-jobs")
      return <><th className="px-4 py-3 text-left">Technician</th><th className="px-4 py-3 text-left">Position</th><th className="px-4 py-3 text-right">Jobs</th></>;
    return <>
      <th className="px-4 py-3 text-left">Asset</th><th className="px-4 py-3 text-left">Site</th>
      <th className="px-4 py-3 text-left">Priority</th><th className="px-4 py-3 text-right">Health</th>
      <th className="px-4 py-3 text-right">RUL (yrs)</th><th className="px-4 py-3 text-right">Renew by</th>
      <th className="px-4 py-3 text-right">Est. cost</th>
    </>;
  };

  const renderRow = (r) => {
    if (metric === "clients-jobs")
      return <><td className="px-4 py-3"><NameCell name={r.client} to={r.client_id ? `/clients/${r.client_id}` : null} navigate={navigate} /></td><td className="px-4 py-3 text-right font-mono">{r.job_count}</td></>;
    if (metric === "clients-revenue")
      return <><td className="px-4 py-3"><NameCell name={r.client} to={r.client_id ? `/clients/${r.client_id}` : null} navigate={navigate} /></td><td className="px-4 py-3 text-right font-mono">{r.hours}</td><td className="px-4 py-3 text-right font-mono text-[#059669]">{fmtMoney(r.revenue)}</td></>;
    if (metric === "technicians-jobs")
      return <><td className="px-4 py-3"><NameCell name={r.technician} to={r.technician_id ? `/technicians/${r.technician_id}` : null} navigate={navigate} /></td><td className="px-4 py-3 text-neutral-600">{r.position || "—"}</td><td className="px-4 py-3 text-right font-mono">{r.job_count}</td></>;
    return <>
      <td className="px-4 py-3 font-semibold text-[#0A0A0A]">{r.asset_name}</td>
      <td className="max-w-[240px] truncate px-4 py-3 text-neutral-600" title={r.site}>{r.site || "—"}</td>
      <td className="px-4 py-3"><span className={`inline-flex rounded-sm px-2 py-0.5 text-xs font-bold ${BAND[r.priority_band] || "bg-neutral-100 text-neutral-500"}`}>{r.priority_band}</span></td>
      <td className="px-4 py-3 text-right font-mono">{r.health_score}</td>
      <td className="px-4 py-3 text-right font-mono">{r.remaining_useful_life}</td>
      <td className="px-4 py-3 text-right font-mono">{r.recommended_renewal_year}</td>
      <td className="px-4 py-3 text-right font-mono">{fmtMoney(r.replacement_cost)}</td>
    </>;
  };

  const onRowClick = (r) => {
    if (metric === "warnings" && r.asset_id) navigate(`/asset-detail?id=${encodeURIComponent(r.asset_id)}`);
  };

  return (
    <Layout wide>
      <button data-testid="leaderboard-back" onClick={() => navigate("/dashboard")}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0055FF] hover:underline">
        <ArrowLeft size={15} weight="bold" /> Back to dashboard
      </button>

      <div className="mt-4 flex items-center gap-3" data-testid="leaderboard-page">
        <Icon size={30} weight="bold" className={meta.accent} />
        <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter text-[#0A0A0A]">
          {data?.title || "Leaderboard"}
        </h1>
      </div>

      {data === null ? (
        <div className="mt-10 flex h-40 items-center justify-center"><CircleNotch size={28} className="animate-spin text-[#0055FF]" /></div>
      ) : data === false ? (
        <p className="mt-8 text-sm text-[#DC2626]">Failed to load leaderboard.</p>
      ) : (
        <>
          <p className="mt-2 text-sm text-neutral-500">{rows.length.toLocaleString()} ranked {rows.length === 1 ? "entry" : "entries"}.</p>
          <div className="mt-4 overflow-x-auto border border-neutral-200 bg-white shadow-sm rounded-sm">
            <table className="w-full border-collapse text-sm">
              <thead><Header>{renderHead()}</Header></thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={`${r.client_id || r.technician_id || r.asset_id || r.client || r.technician || r.asset_name || "row"}-${i}`} data-testid={`leaderboard-row-${i}`} onClick={() => onRowClick(r)}
                    className={`border-b border-neutral-100 even:bg-neutral-50/60 ${metric === "warnings" && r.asset_id ? "cursor-pointer hover:bg-[#0055FF]/5" : ""}`}>
                    <td className="px-4 py-3 font-bold text-neutral-400">{i + 1}</td>
                    {renderRow(r)}
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-neutral-400">No data yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Layout>
  );
}
